/** Google OAuth and Drive Picker; access tokens stay in memory, never browser storage. */
const SCOPE = "https://www.googleapis.com/auth/drive.readonly";
const IMAGE_MIMES = new Set(["image/jpeg", "image/png", "image/heic", "image/heif"]);
const FOLDER_MIME = "application/vnd.google-apps.folder";
const scripts = new Map();

function loadScript(src) {
  if (scripts.has(src)) return scripts.get(src);
  const task = new Promise((resolve, reject) => {
    const element = document.createElement("script");
    element.src = src; element.async = true;
    element.onload = resolve;
    element.onerror = () => reject(new Error("Google libraries could not load."));
    document.head.appendChild(element);
  });
  scripts.set(src, task);
  return task;
}

export function hasGoogleDriveConfiguration() {
  return Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID && process.env.REACT_APP_GOOGLE_API_KEY);
}

/** Recursive folder expansion, capped at 101 results to detect oversize selections. */
export async function resolveDriveSelection(docs, token, fetchImpl = fetch) {
  const ids = [];
  const seenFiles = new Set();
  const seenFolders = new Set();
  async function addImage(id) {
    if (!seenFiles.has(id)) { seenFiles.add(id); ids.push(id); }
    if (ids.length > 100) throw new Error("Choose no more than 100 Drive images per import.");
  }
  async function visitFolder(folderId) {
    if (seenFolders.has(folderId)) return;
    seenFolders.add(folderId);
    let pageToken;
    do {
      const params = new URLSearchParams({
        q: `'${folderId.replace(/\\/g, "\\\\").replace(/'/g, "\\'")}' in parents and trashed = false`,
        fields: "nextPageToken,files(id,mimeType,name)", pageSize: "100",
        supportsAllDrives: "true", includeItemsFromAllDrives: "true"
      });
      if (pageToken) params.set("pageToken", pageToken);
      const response = await fetchImpl(`https://www.googleapis.com/drive/v3/files?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`Google Drive folder listing failed (${response.status}).`);
      const payload = await response.json();
      for (const child of payload.files || []) {
        if (child.mimeType === FOLDER_MIME) await visitFolder(child.id);
        else if (IMAGE_MIMES.has(child.mimeType)) await addImage(child.id);
      }
      pageToken = payload.nextPageToken;
    } while (pageToken);
  }
  for (const doc of docs || []) {
    if (doc.mimeType === FOLDER_MIME) await visitFolder(doc.id);
    else if (IMAGE_MIMES.has(doc.mimeType)) await addImage(doc.id);
  }
  return ids;
}

export async function pickDriveImages() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  const appId = process.env.REACT_APP_GOOGLE_APP_ID;
  if (!clientId || !apiKey) throw new Error("Configure REACT_APP_GOOGLE_CLIENT_ID and REACT_APP_GOOGLE_API_KEY.");
  await Promise.all([
    loadScript("https://accounts.google.com/gsi/client"),
    loadScript("https://apis.google.com/js/api.js")
  ]);
  await new Promise((resolve, reject) => window.gapi.load("picker", {
    callback: resolve, onerror: () => reject(new Error("Google Picker could not initialize."))
  }));
  const accessToken = await new Promise((resolve, reject) => {
    const oauth = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId, scope: SCOPE,
      callback: (result) => result.error ? reject(new Error(result.error_description || result.error)) : resolve(result.access_token),
      error_callback: (error) => reject(new Error(error.message || "Google authorization cancelled."))
    });
    oauth.requestAccessToken({ prompt: "consent" });
  });
  if (!accessToken) throw new Error("No Google OAuth access token returned.");
  const selected = await new Promise((resolve, reject) => {
    const picker = window.google.picker;
    const view = new picker.DocsView(picker.ViewId.DOCS)
      .setIncludeFolders(true).setSelectFolderEnabled(true);
    let builder = new picker.PickerBuilder()
      .setOAuthToken(accessToken).setDeveloperKey(apiKey)
      .setTitle("Select event photos or a Drive folder")
      .addView(view).enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .setCallback((data) => {
        if (data.action === picker.Action.CANCEL) resolve(null);
        else if (data.action === picker.Action.PICKED) resolve(data.docs || []);
      });
    if (appId) builder = builder.setAppId(appId);
    try { builder.build().setVisible(true); }
    catch (error) { reject(error); }
  });
  if (!selected) return null;
  const fileIds = await resolveDriveSelection(selected, accessToken);
  if (!fileIds.length) throw new Error("Selected Drive files and folders contain no supported images.");
  return { accessToken, fileIds };
}
